import { useCallback, useEffect, useRef, useState } from "react";
import {
  getCorporation,
  toggleShopMaintenance,
  updateMaintenanceMessage,
} from "../../../../../../api/configuration";
import { useSavedForm, SaveFooter, LoadState } from "./savedForm";
const get = async () => {
  const response = await getCorporation();
  const raw = response?.data?.corporation;
  if (response?.error !== false || typeof raw?.shopIsActive !== "boolean")
    throw new Error();
  return raw;
};
const read = async () => {
  const raw = await get();
  return { raw, draft: { message: raw.maintenanceMessage || "" } };
};
const write = (draft) => updateMaintenanceMessage(draft.message);
export const Maintenance = () => {
  const form = useSavedForm(read, write);
  const [active, setActive] = useState(null);
  const [desired, setDesired] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pending = useRef(false);
  const activeRef = useRef(null);
  useEffect(() => {
    if (form.record) {
      const previousActive = activeRef.current;
      const nextActive = form.record.raw.shopIsActive;
      setDesired((previous) =>
        previous === null || previous === previousActive
          ? nextActive
          : previous,
      );
      activeRef.current = nextActive;
      setActive(nextActive);
    }
  }, [form.record]);
  const apply = useCallback(async () => {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      // The existing endpoint toggles. Re-read before every attempt so a lost
      // response/retry cannot invert a status that was already applied.
      const current = await get();
      if (current.shopIsActive !== desired) {
        try {
          await toggleShopMaintenance();
        } catch {
          /* read back below */
        }
      }
      const confirmed = await get();
      activeRef.current = confirmed.shopIsActive;
      setActive(confirmed.shopIsActive);
      if (confirmed.shopIsActive !== desired) throw new Error();
    } catch {
      setError(
        "No se ha podido confirmar el estado de la tienda. Vuelve a consultar antes de intentarlo de nuevo.",
      );
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }, [desired]);
  if (!form.record) return <LoadState form={form} label="mantenimiento" />;
  return (
    <div className="generalReferenceForm">
      <div className="generalFormBody">
        <label className="maintenanceSwitch">
          <span>
            <b>Mantenimiento de tienda online</b>
            <small>
              {active === null
                ? "Consultando estado…"
                : active
                  ? "La tienda online está operativa."
                  : "La tienda online está en mantenimiento."}
            </small>
          </span>
          <input
            role="switch"
            aria-label="Mantenimiento de tienda online"
            type="checkbox"
            checked={desired === false}
            disabled={busy || form.saving || active === null}
            onChange={(e) => setDesired(!e.target.checked)}
          />
        </label>
        {desired !== active && (
          <div className="maintenanceApply">
            <span>Cambio de estado pendiente.</span>
            <button
              type="button"
              disabled={busy || form.saving}
              onClick={apply}
            >
              {busy
                ? "Comprobando…"
                : desired
                  ? "Desactivar mantenimiento"
                  : "Activar mantenimiento"}
            </button>
          </div>
        )}
        {error && <p role="alert">{error}</p>}
      </div>
      <form onSubmit={form.save} aria-busy={form.saving}>
        <fieldset disabled={form.saving || busy}>
          <div className="generalFormBody maintenanceDetails">
            <label htmlFor="maintenance-message">
              Mensaje de alerta
              <textarea
                id="maintenance-message"
                required
                value={form.draft.message}
                onChange={(e) => form.setDraft({ message: e.target.value })}
              />
            </label>
            <p>
              Se muestra a los usuarios mientras la tienda está en
              mantenimiento.
            </p>
          </div>
          <SaveFooter {...form} label="Guardar mensaje" />
        </fieldset>
      </form>
    </div>
  );
};
